/**
 * Item Use Toast
 *
 * Brief notification shown when an item is used during raid draft.
 * Auto-dismisses after a short delay.
 */

import { useEffect, useState } from "react";
import classNames from "classnames";

export interface ItemUseToastData {
  id: string;
  playerName: string;
  itemName: string;
  pokemonName: string;
  isHeal: boolean;
}

interface ItemUseToastProps {
  toast: ItemUseToastData;
  onDismiss: (id: string) => void;
}

const DISPLAY_DURATION = 3000;

export const ItemUseToast = ({ toast, onDismiss }: ItemUseToastProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => setIsVisible(true));

    // Auto-dismiss after duration
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onDismiss(toast.id), 300);
    }, DISPLAY_DURATION);

    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  // Format item name for display
  const formatItemName = (name: string) =>
    name
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // Format Pokemon name for display
  const formatPokemonName = (name: string) =>
    name.charAt(0).toUpperCase() + name.slice(1);

  return (
    <div
      className={classNames(
        "flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg transition-all duration-300",
        "bg-pk-gray-2 border-2",
        {
          "border-pk-green-4": toast.isHeal,
          "border-pk-yellow-4": !toast.isHeal,
          "opacity-100 translate-y-0": isVisible,
          "opacity-0 translate-y-2": !isVisible,
        },
      )}
    >
      {/* Icon */}
      <div
        className={classNames("w-6 h-6 rounded-full flex items-center justify-center text-sm", {
          "bg-pk-green-4": toast.isHeal,
          "bg-pk-yellow-4": !toast.isHeal,
        })}
      >
        {toast.isHeal ? "💚" : "✨"}
      </div>

      {/* Message */}
      <div className="text-sm">
        <span className="text-pk-blue-4 font-medium">{toast.playerName}</span>
        <span className="text-white"> used </span>
        <span className="text-pk-green-4 font-medium">{formatItemName(toast.itemName)}</span>
        <span className="text-white"> on </span>
        <span className="text-pk-yellow-3 font-medium">{formatPokemonName(toast.pokemonName)}</span>
      </div>
    </div>
  );
};

// ============================================================================
// Toast Container
// ============================================================================

interface ItemUseToastContainerProps {
  toasts: ItemUseToastData[];
  onDismiss: (id: string) => void;
}

export const ItemUseToastContainer = ({ toasts, onDismiss }: ItemUseToastContainerProps) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <ItemUseToast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

